import { useEffect, useRef, useState } from "react";

/**
 * Shows its children once they reach the viewport.
 *
 * Deliberately the plainest motion on the page — a short lift and a fade, the
 * same for every section. The hero's assembly is the moment worth watching;
 * if each section arrived with a flourish of its own they would compete with
 * it and with each other.
 *
 * Two environments get the content immediately rather than an empty page: one
 * without `IntersectionObserver` (jsdom, during tests), and one asking for
 * reduced motion, where the CSS also drops the transform.
 */
function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          // Revealing is a one-way door: scrolling back up must not replay it.
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`lp-reveal${isVisible ? " is-in" : ""}${className ? ` ${className}` : ""}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
